
import React, { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Lead, LeadType, LeadStatus, LeadOutcome } from '../types/LeadTypes';
import { getStatusColor, getStatusIcon } from '../LeadStatus';
import OutcomeSelect from '../LeadOutcome';
import ValueInput from '../ValueInput';
import AudioPlayback from '../AudioPlayback';
import FormFields from '../FormFields';
import { getLeadTypeIcon, getLeadTypeColor, getFilteredStatusOptions, getOutcomeCircleColor } from '../LeadUtils';
import { cn } from "@/lib/utils";
import LeadItemDetails from './LeadItemDetails';

interface LeadListItemProps {
  lead: Lead;
  onStatusChange: (id: string, status: LeadStatus) => void;
  onOutcomeChange: (id: string, outcome: LeadOutcome | null) => void;
  onValueChange: (id: string, value: string) => void;
  onNameChange: (id: string, name: string) => void;
  openNameModal: (id: string) => void;
  openTranscriptModal: (id: string) => void;
  isExpanded?: boolean;
  onToggleExpand?: (id: string) => void;
}

const LeadListItem: React.FC<LeadListItemProps> = ({
  lead,
  onStatusChange,
  onOutcomeChange,
  onValueChange,
  onNameChange,
  openNameModal,
  openTranscriptModal,
  isExpanded = false,
  onToggleExpand
}) => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);

  const toggleAccordion = () => {
    if (onToggleExpand) {
      onToggleExpand(lead.id);
    }
  };

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
    window.location.href = `tel:${lead.phone}`;
  };

  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
    openNameModal(lead.id);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
  };

  return (
    <Accordion 
      type="single" 
      collapsible 
      className="border-b border-gray-100 last:border-b-0 w-full"
      value={isExpanded ? lead.id : ""}
      onValueChange={(value) => toggleAccordion()}
    >
      <AccordionItem value={lead.id} className="border-0">
        <div 
          className="flex flex-col py-4 px-4 w-full cursor-pointer" 
          onClick={toggleAccordion}
        >
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-3" style={{
                backgroundColor: getOutcomeCircleColor(lead.outcome)
              }} />
              <a 
                href={`tel:${lead.phone}`} 
                className="text-base hover:underline"
                onClick={handlePhoneClick}
              >
                {lead.phone}
              </a>
            </div>
            <div className="flex items-center">
              <DropdownMenu open={openDropdown} onOpenChange={setOpenDropdown}>
                <DropdownMenuTrigger asChild>
                  <span 
                    className={`${getLeadTypeColor(lead.status)} cursor-pointer hover:underline flex items-center text-sm`}
                    onClick={handleStatusClick}
                  >
                    {getLeadTypeIcon(lead.type, lead.status)}
                    <span className="ml-1.5">{lead.status}</span>
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-gray-200">
                  {getFilteredStatusOptions(lead.type).map((statusOption) => (
                    <DropdownMenuItem 
                      key={statusOption} 
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion from toggling
                        onStatusChange(lead.id, statusOption as LeadStatus);
                        setOpenDropdown(false); // Auto close after selection
                      }}
                    >
                      <span className={cn("inline-flex items-center px-2 py-1 rounded-full text-xs", getStatusColor(statusOption as LeadStatus))}>
                        <span className="mr-1">{getStatusIcon(statusOption as LeadStatus)}</span>
                        {statusOption}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {isExpanded ? (
                <ChevronUp size={18} className="ml-2 text-gray-400" />
              ) : (
                <ChevronDown size={18} className="ml-2 text-gray-400" />
              )}
            </div>
          </div>
          <div className="flex justify-between items-center w-full pt-2">
            <div className="flex items-center">
              <button 
                className="text-sm text-gray-500 flex items-center"
                onClick={handleNameClick}
              >
                <span>{lead.name}</span>
              </button>
            </div>
            <div className="text-xs text-gray-400">
              {lead.date}
            </div>
          </div>
        </div>
        <AccordionContent className="pb-4 px-4">
          <LeadItemDetails 
            lead={lead}
            onOutcomeChange={(value) => onOutcomeChange(lead.id, value)}
            onValueChange={(value) => onValueChange(lead.id, value)}
            openTranscriptModal={() => openTranscriptModal(lead.id)}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default LeadListItem;
